/**
 * 「URLから読み取る」パネル (docs/features/product-url-crawl-summary-plan.md §4.6・§4.7)。
 * 案件の新規登録・編集フォームの上部に置く。URL を入力して読み取りジョブを投入し、完了したら提案を
 * 「現在の値 / 提案値」の差分で見せ、項目ごとに採用してフォームへ反映する (保存は利用者の「保存」操作)。
 *
 * - 既定の採用は提案を反映する時点のフォーム値で判定する (未入力=採用・入力済み=据え置き)
 * - 矛盾 (conflicts) のある項目は提案値を出さず、両方の値から利用者が選ぶ
 * - ブランドは brand_match に応じて「一致しそう (既定採用)」「候補」「新規ブランドとして作成」を出し、brand_id と brand_name を一組で入れる
 * - 処理中のジョブIDはユーザー・組織・案件単位で localStorage に保持し、ページを離れて戻ったときは続きをポーリングして結果を表示する
 *   (採用・中止・失敗・保存成功で破棄。サーバー側のジョブは画面を離れても止まらない)
 * - 画面を離れる (アンマウント) のと「中止」は区別する。受付APIの応答前に離れても、受付済みのジョブIDは保存して戻ったときに続きを出す。
 *   「中止」と別URLでの再開始だけが、受付済みでも保存しない
 * - 処理中は読み込み予定ページの一覧を出し、ページの除外と「ここまででOK」(残りを読まずに要約へ進む) を指示できる (§4.4b)。
 *   指示は手元の集合を正とし、送信中でなければ進捗に載るサーバー側の指示 (control) で表示を合わせ直す (再読み込み・ページ移動後の復元)
 * - 2ステップ (計画 §14.8): 1 読み取り (URL入力と進捗) → 2 反映 (読み取り結果の採用)。結果の画面には URL 欄と「読み取る」を出さず、
 *   1回30ptの読み取りを誤って繰り返さないようにする。ステップの移動はステッパーだけで行い、1 に戻っても新しい読み取りを受け付けるまでは
 *   前の結果を残してステッパーの 2 で戻れる
 */
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, type Ref } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Check, ChevronRight, CircleMinus, Equal, Info, Link2, Loader2, MoveRight, Slash, Undo2 } from 'lucide-react';
import { brandConfig } from '../../../config/brands';
import { GenerateButton } from '../../../components/generation/common/GenerateButton';
import { ArrowSteps } from '../../../components/common/ArrowSteps';
import { JobStepper } from '../../../components/common/JobStepper';
import { Tooltip } from '../../../components/common/Tooltip';
import { useCreditBadgeText } from '../../../components/generation/hooks/useGenerationCredits';
import { useCreditRates } from '../../../features/organizations/hooks';
import { useUrlSummaryResumeScope } from '../../../features/products/useUrlSummaryResumeScope';
import {
  URL_SUMMARY_GIVE_UP_AFTER_MS,
  URL_SUMMARY_GIVE_UP_NOTICE,
  URL_SUMMARY_SLOW_AFTER_MS,
  URL_SUMMARY_SLOW_NOTICE,
  VIDEO_SUMMARY_STAGE_ORDER,
  VIDEO_SUMMARY_STEPPER_LABELS,
  applyAppeals,
  brandPair,
  buildAdoption,
  buildFieldItems,
  buildFillAllAdoption,
  buildPlannedPageRows,
  buildSourcePages,
  cancelProductSummary,
  cancelProductUrlSummary,
  clearUrlSummaryResume,
  controlProductUrlSummary,
  createProductUrlSummaryJob,
  createProductVideoSummaryJob,
  formatUrlSummaryElapsed,
  loadUrlSummaryResume,
  normalizeSummaryUrl,
  planBrandAdoption,
  saveUrlSummaryResume,
  summaryCreditsPerRun,
  summarySourceLabel,
  urlSummaryJobErrorMessage,
  urlSummaryProgressText,
  urlSummaryStages,
  URL_SUMMARY_STEPPER_LABELS,
  urlSummaryRequestErrorMessage,
  waitForProductUrlSummary,
  URL_SUMMARY_CONTROL_STAGES,
  URL_SUMMARY_PAGE_STATE_LABELS,
  type SourcePages,
  type UrlSummaryAdoption,
  type UrlSummaryBrandCandidate,
  type UrlSummaryBrandMatch,
  type UrlSummaryFieldItem,
  type UrlSummaryFormSnapshot,
  type UrlSummaryNewBrandDraft,
  type UrlSummaryProgress,
  type UrlSummaryResult,
  type UrlSummarySource,
} from '../../../features/products/urlSummary';
import './ProductUrlSummaryPanel.css';

export interface ProductUrlSummaryApply {
  adoption: UrlSummaryAdoption;
  sourcePages: SourcePages | null;
  brand: { brand_id: string; brand_name: string } | null;
  /** 読み取ったブランドの照合結果 (新規登録の入口が「新規ブランドとして登録しますか」を出すのに使う) */
  brandMatch?: UrlSummaryBrandMatch | null;
  /** 完了直後の自動反映 (autoApply) か、利用者が「反映」を押したか */
  auto?: boolean;
}

interface Props {
  /** 既存案件の ID (API に product_id として送る・引き継ぎ情報のキー)。新規登録の下書きでは渡さず draftId を使う */
  productId?: string | null;
  /** 新規登録の下書き ID。引き継ぎ情報 (localStorage) のキーにだけ使い、API には送らない (案件行はまだ無い) */
  draftId?: string | null;
  /** 反映時点のフォーム値 (既定の採用判定に使う) */
  getForm: () => UrlSummaryFormSnapshot;
  brands: ReadonlyArray<{ id: string; brand_name: string }>;
  /** 採用した項目をフォームへ反映する */
  onApply: (apply: ProductUrlSummaryApply) => void;
  /** 「新規ブランドとして作成」(権限が無ければ undefined にして文言だけ出す) */
  onCreateBrand?: (draft: UrlSummaryNewBrandDraft) => void;
  disabled?: boolean;
  /** モーダルの中に置く (見出しはモーダル側が出すので、パネル自身の見出しと枠を出さない) */
  embedded?: boolean;
  /** 見出し「URLから読み取る」だけを出さない (新規登録の入口。ページの見出しと重なるため) */
  hideTitle?: boolean;
  /** 読み取り中かどうかが変わったとき (モーダルの × に「閉じても続きます」を出すのに使う) */
  onRunningChange?: (running: boolean) => void;
  /** 入口画面で入力した URL (新規登録の URL 経路)。autoStart と組で、表示直後に読み取りを始める */
  initialUrl?: string;
  autoStart?: boolean;
  /**
   * 完了したら提案をその場で onApply する (新規登録の入口・入口フロー文書 §6.3 の 2)。フォームは空なので分類も含めて
   * 全部入れ、食い違いは先頭の候補を入れる (buildFillAllAdoption)。ブランドは高信頼で一致したときだけ。
   * 反映後は提案の表を出さず、読み取り元と注記の要約だけにする (表は「詳細」で開ける)
   */
  autoApply?: boolean;
  /** 段階 (1 読み取り / 2 反映) が変わったとき (ページ側で「別のURLで読み直す」を出し分けるのに使う) */
  onStepChange?: (step: UrlSummaryStepNo) => void;
  /** 反映後の要約表示の中に「別のURLで読み直す」を出さない (ページ側の戻るの下に出すとき) */
  hideRereadAction?: boolean;
  /** ページ側から読み直し (段階1 へ) を呼ぶための口 */
  ref?: Ref<ProductUrlSummaryPanelHandle>;
  /**
   * 読み取りの元。省略時は URL (入力欄を出す)。'video' は動画エディタに取り込み済みの収録動画
   * (video-editor-presentation-import-plan.md §4.6): URL 欄を出さず、その動画の文字起こしから読み取る。
   * 結果の形は同じで、提案の採用・ブランド照合も同じ。加えて訴求候補 (原稿の要点) を出し、採用した分を説明文の【訴求】に入れる
   */
  source?: UrlSummarySource;
}

export interface ProductUrlSummaryPanelHandle {
  /** 段階1 (URL の入力) に戻す。反映済みのフォームと結果はそのまま (段階2 へ戻れる) */
  reread: () => void;
}

type Phase = 'idle' | 'running' | 'done' | 'error';

/**
 * ジョブの期限 (result_data.deadline_at・作成から10分) をこれだけ過ぎても pending / processing のままなら「時間内に終わりませんでした」を出す
 * (計画 §16 の残課題・2026-10-31)。本番では Cloud Tasks の再配送で後から失敗に確定することがあるのでポーリングは止めず、案内だけ出して
 * 既存の「中止」へ導く。猶予は、期限ちょうどに打ち切られたジョブの失敗が届くまでの揺れを吸収するため
 */
const URL_SUMMARY_DEADLINE_GRACE_MS = 60 * 1000;
const URL_SUMMARY_DEADLINE_NOTICE = '時間内に終わりませんでした。「中止」を押してから、もう一度読み取りを始められます';

export type UrlSummaryStepNo = 1 | 2;
const URL_SUMMARY_STEPS: ReadonlyArray<{ step: UrlSummaryStepNo; label: string }> = [
  { step: 1, label: '読み取り' },
  { step: 2, label: '反映' },
];

/** 画面側の待機をやめた理由。'cancel' (中止・別URLで再開始) だけは受付済みのジョブIDも保存しない。それ以外 (離脱) は保存する */
type AbortReason = 'cancel' | 'leave';

function abortedBy(controller: AbortController, reason: AbortReason): boolean {
  return controller.signal.aborted && controller.signal.reason === reason;
}

export function ProductUrlSummaryPanel({ productId, draftId, getForm, brands, onApply, onCreateBrand, disabled, embedded, hideTitle, onRunningChange, initialUrl, autoStart, autoApply, onStepChange, hideRereadAction, ref, source }: Props) {
  // 再読み込み後は props に元が無いので、引き継ぎ情報 (localStorage) に残した元で補う
  const [resumedSource, setResumedSource] = useState<UrlSummarySource | null>(null);
  const effectiveSource = source ?? resumedSource ?? undefined;
  const kind = effectiveSource?.kind ?? 'url';
  // 引き継ぎ情報のキー: 既存案件は案件 ID・新規登録は下書き ID (無ければ 'new')
  const resumeId = productId ?? draftId ?? null;
  const [url, setUrl] = useState(initialUrl ?? '');
  const autoStartedRef = useRef(false);
  const [phase, setPhase] = useState<Phase>('idle');
  useEffect(() => {
    onRunningChange?.(phase === 'running');
  }, [phase, onRunningChange]);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [elapsed, setElapsed] = useState('');
  const [slowNotice, setSlowNotice] = useState<string | null>(null);
  // ジョブの期限を猶予分過ぎても終わっていない (phase が running = ジョブが pending / processing のまま)
  const [deadlinePassed, setDeadlinePassed] = useState(false);
  const [result, setResult] = useState<{ jobId: string; data: UrlSummaryResult } | null>(null);
  const [items, setItems] = useState<UrlSummaryFieldItem[]>([]);
  const [adopted, setAdopted] = useState<Set<string>>(new Set());
  const [chosen, setChosen] = useState<Map<string, string>>(new Map());
  const [brandChoice, setBrandChoice] = useState<string>('');
  // 訴求候補 (動画からの読み取りだけ)。既定で全部採用し、採用した分を説明文の末尾に【訴求】で足す
  const [adoptedAppeals, setAdoptedAppeals] = useState<Set<string>>(new Set());
  const [applied, setApplied] = useState(false);
  const [cancelNote, setCancelNote] = useState<string | null>(null);
  // 自動反映した結果を要約表示にしている (表は折りたたみ)
  const [compact, setCompact] = useState(false);
  const [appliedCount, setAppliedCount] = useState(0);
  // 結果があるのに 1 (読み取り) を開いている (ステッパーで戻った)
  const [rereading, setRereading] = useState(false);
  // 読み込み予定ページ (§4.4b)
  const [jobId, setJobId] = useState<string | null>(null);
  const [live, setLive] = useState<UrlSummaryProgress | null>(null);
  const [excludedPages, setExcludedPages] = useState<Set<number>>(new Set());
  const [finishRequested, setFinishRequested] = useState(false);
  const [controlError, setControlError] = useState<string | null>(null);
  const controlInFlight = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  // 引き継ぎ情報の持ち主 (プロファイル取得前は保存も復元もしない)
  const scope = useUrlSummaryResumeScope();
  const scopeKey = scope ? `${scope.userUid}:${scope.organizationId ?? ''}` : null;
  // 1回のポイント数 (計画 §14.6)。取れるまで・未設定のときは読み取りを始めない
  const creditRates = useCreditRates();
  const creditsPerRun = summaryCreditsPerRun(creditRates.data?.rates, kind);
  const creditBadge = useCreditBadgeText(creditsPerRun);
  const queryClient = useQueryClient();
  // 控除 (受付) と返金 (失敗) の後に残高を取り直す
  const refreshCredits = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['organization', 'credits'] });
    void queryClient.invalidateQueries({ queryKey: ['credits'] });
  }, [queryClient]);

  // 画面を離れたら待機をやめる (ジョブ自体は続く)。開発モードの StrictMode はマウント直後に効果を一度アンマウント扱いで
  // 片付けてから再実行するので、その場で中断すると自動開始した受付の応答を捨てて「実行中」のまま止まる。1 ティック遅らせて、
  // 本当に外れたままのときだけ中断する
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      window.setTimeout(() => {
        if (!mountedRef.current) abortRef.current?.abort('leave' satisfies AbortReason);
      }, 0);
    };
  }, []);

  // 経過時間の表示と注意 (10分 / 2時間)・ジョブの期限超過 (deadline_at + 猶予)
  const deadlineAt = live?.deadline_at ?? null;
  useEffect(() => {
    if (phase !== 'running' || startedAt == null) {
      setSlowNotice(null);
      setElapsed('');
      setDeadlinePassed(false);
      return;
    }
    const deadlineMs = deadlineAt ? Date.parse(deadlineAt) : Number.NaN;
    const tick = () => {
      const ms = Date.now() - startedAt;
      setElapsed(formatUrlSummaryElapsed(ms));
      setSlowNotice(ms >= URL_SUMMARY_GIVE_UP_AFTER_MS ? URL_SUMMARY_GIVE_UP_NOTICE : ms >= URL_SUMMARY_SLOW_AFTER_MS ? URL_SUMMARY_SLOW_NOTICE : null);
      setDeadlinePassed(Number.isFinite(deadlineMs) && Date.now() - deadlineMs >= URL_SUMMARY_DEADLINE_GRACE_MS);
    };
    tick();
    const id = window.setInterval(tick, 1_000);
    return () => window.clearInterval(id);
  }, [phase, startedAt, deadlineAt]);

  const brandPlan = useMemo(() => planBrandAdoption(result?.data.brand_match, getForm().brand_id, brands), [result, brands, getForm]);
  const currentBrandName = useMemo(() => {
    const id = getForm().brand_id;
    return id ? brands.find((b) => b.id === id)?.brand_name ?? '' : '';
  }, [result, brands, getForm]); // eslint-disable-line react-hooks/exhaustive-deps -- 提案を反映した時点のフォーム値で出す
  // 読み取り結果が選択中のブランドと同じ (採用しても変わらない)
  const brandUnchanged = useMemo(() => {
    const id = getForm().brand_id;
    return !!id && brandPlan.candidates[0]?.brand_id === id;
  }, [brandPlan, getForm]);
  // 採用候補として選べるブランド (選択中のブランドと別の読み取り結果なら据え置く・同じなら変わらないので選ばせない)
  const brandOptions = brandPlan.keptExisting || brandUnchanged ? [] : brandPlan.candidates;
  const toggleBrand = (on: boolean) => {
    setBrandChoice(on ? (brandPlan.defaultCandidate ?? brandOptions[0])?.brand_id ?? '' : '');
  };

  const reflect = useCallback((jobId: string, data: UrlSummaryResult) => {
    const form = getForm();
    const built = data.proposal ? buildFieldItems(data.proposal, form) : [];
    const defaultKeys = new Set(built.filter((i) => i.adopt).map((i) => i.key));
    setItems(built);
    setAdopted(defaultKeys);
    setChosen(new Map());
    const plan = planBrandAdoption(data.brand_match, form.brand_id, brands);
    setBrandChoice(plan.defaultCandidate?.brand_id ?? '');
    const appeals = data.proposal?.appeal_candidates ?? [];
    setAdoptedAppeals(new Set(appeals));
    setResult({ jobId, data });
    setRereading(false);
    setPhase('done');
    if (autoApply && data.proposal) {
      // 新規登録: 空のフォームに提案を全部入れる (分類も・食い違いは先頭)。表は折りたたんで要約だけ出す
      const fill = buildFillAllAdoption(built);
      setAdopted(fill.adopted);
      setChosen(fill.chosen);
      const adoption = applyAppeals(buildAdoption(built, fill.adopted, fill.chosen), appeals, form.description);
      const brand = plan.defaultCandidate ? brandPair(plan.defaultCandidate.brand_id, brands) : null;
      const keys = [...adoption.adoptedKeys];
      if (brand) keys.push('brand_id');
      const sourcePages = buildSourcePages(data, keys, data.proposal.field_sources, jobId);
      onApply({ adoption, sourcePages, brand, brandMatch: data.brand_match ?? null, auto: true });
      if (scope) clearUrlSummaryResume(scope, resumeId);
      setApplied(true);
      setAppliedCount(keys.length);
      setCompact(true);
      return;
    }
    setApplied(false);
    setCompact(false);
  }, [getForm, brands, autoApply, onApply, scope, resumeId]);

  const resetPlanned = (id: string | null) => {
    setJobId(id);
    setLive(null);
    setExcludedPages(new Set());
    setFinishRequested(false);
    setControlError(null);
  };

  const onJobProgress = useCallback((rd: UrlSummaryProgress | null) => {
    setLive(rd);
    // 送信中でなければサーバー側の指示に合わせる (別タブ・再読み込み後の復元)
    if (rd?.control && controlInFlight.current === 0) {
      setExcludedPages(new Set(rd.control.excluded_page_ids));
      setFinishRequested(rd.control.finish_fetch);
    }
  }, []);

  /** ジョブの完了を待って反映する。失敗したら引き継ぎ情報を捨てる (結果は採用するまで保持する) */
  const track = useCallback(async (jobId: string, controller: AbortController) => {
    try {
      const data = await waitForProductUrlSummary(jobId, {
        signal: controller.signal,
        onProgress: (job) => {
          if (controller.signal.aborted) return;
          setProgress(urlSummaryProgressText(job, kind));
          onJobProgress((job.result_data ?? null) as UrlSummaryProgress | null);
        },
      });
      if (controller.signal.aborted) return;
      reflect(jobId, data);
    } catch (err) {
      if (controller.signal.aborted) return;
      if (scope) clearUrlSummaryResume(scope, resumeId);
      setPhase('error');
      setError(urlSummaryJobErrorMessage(err));
      // 失敗したジョブはポイントが返る
      refreshCredits();
    }
  }, [scope, resumeId, reflect, onJobProgress, refreshCredits, kind]);

  // ページを離れていた間のジョブを引き継ぐ (処理中なら続きをポーリング・完了済みなら結果をそのまま表示)
  useEffect(() => {
    if (!scope) return;
    const resume = loadUrlSummaryResume(scope, resumeId);
    if (!resume) return;
    const controller = new AbortController();
    abortRef.current?.abort('leave' satisfies AbortReason);
    abortRef.current = controller;
    setUrl(resume.url);
    setResumedSource(resume.source ?? null);
    setError(null);
    resetPlanned(resume.jobId);
    setPhase('running');
    setProgress('前回の読み取りを引き継いでいます');
    setStartedAt(resume.startedAt);
    void track(resume.jobId, controller);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 初回表示 (案件か持ち主が変わったとき) だけ
  }, [resumeId, scopeKey]);

  const start = async () => {
    // 持ち主が決まる前に投入すると受付済みのジョブIDを保存できず、離脱後に復元できないので始めない (ボタンも無効にしている)
    const resumeScope = scope;
    if (!resumeScope) {
      setError('利用者情報を取得しています。少し待ってからもう一度お試しください');
      return;
    }
    if (creditsPerRun === null) {
      setError('料金を取得しています。少し待ってからもう一度お試しください');
      return;
    }
    const normalized = source?.kind === 'video' ? '' : normalizeSummaryUrl(url);
    if (normalized == null) {
      setError('URLの形式が正しくありません');
      return;
    }
    setError(null);
    setCancelNote(null);
    resetPlanned(null);
    setPhase('running');
    setProgress('読み取りを開始しています');
    const startedAtMs = Date.now();
    setStartedAt(startedAtMs);
    const controller = new AbortController();
    // 別URLで再開始したときは前のジョブを「中止」扱いにする (応答が後から来ても新しいジョブの引き継ぎ情報を上書きしない)
    abortRef.current?.abort('cancel' satisfies AbortReason);
    abortRef.current = controller;
    // 受付APIの応答前に画面を離れてもジョブIDを残せるよう、持ち主 (resumeScope) と案件は投入時点のものを使う
    let jobId: string;
    try {
      const form = getForm();
      const created = source?.kind === 'video'
        ? await createProductVideoSummaryJob({
          project_id: source.projectId, product_id: productId ?? null, hint_title: form.title.trim() || null, quoted_credits: creditsPerRun,
        })
        : await createProductUrlSummaryJob({
          url: normalized, product_id: productId ?? null, hint_title: form.title.trim() || null, quoted_credits: creditsPerRun,
        });
      jobId = created.job_id;
      // 受付済みなら中止していても控除されているので、残高は取り直す
      refreshCredits();
    } catch (err) {
      if (controller.signal.aborted) return;
      setPhase('error');
      setError(urlSummaryRequestErrorMessage(err));
      return;
    }
    if (abortedBy(controller, 'cancel')) return;
    // 受け付けた時点で前の結果を捨てる。中止の判定より後に置く (応答を待つ間に中止してステッパーの 2 で戻った結果を、
    // 遅れて届いた応答で消さないため)。受付に失敗したときもステッパーの 2 で戻れるよう残す
    setResult(null);
    setItems([]);
    setApplied(false);
    setJobId(jobId);
    // サーバー側では受付済み。画面を離れた後でも保存し、戻ったときに続きを出す
    saveUrlSummaryResume(resumeScope, resumeId, { jobId, url: normalized, startedAt: startedAtMs, ...(source?.kind === 'video' ? { source } : {}) });
    if (controller.signal.aborted) return;
    await track(jobId, controller);
  };

  // 入口画面で URL を入れて「読み取る」を押した (または動画を選んだ) 場合: 持ち主と料金が取れたら 1 回だけ自動で始める
  useEffect(() => {
    if (!autoStart || autoStartedRef.current || !(initialUrl || source?.kind === 'video')) return;
    if (!scope || creditsPerRun === null || phase !== 'idle') return;
    if (loadUrlSummaryResume(scope, resumeId)) return; // 引き継ぎがあればそちらを優先
    autoStartedRef.current = true;
    void start();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 開始条件が揃った最初の 1 回だけ
  }, [autoStart, initialUrl, source?.kind, scope, creditsPerRun, phase]);

  const cancel = async () => {
    // 画面の待機をやめ、サーバー側の読み取りも打ち切る (利用者決定 2026-10-30。別画面への遷移やタブを閉じただけでは続く)。
    // 実行前なら返金され、実行中なら返金されない (外部 API の原価が発生しているため)
    abortRef.current?.abort('cancel' satisfies AbortReason);
    if (scope) clearUrlSummaryResume(scope, resumeId);
    setPhase('idle');
    setProgress('');
    setError(null);
    const target = jobId;
    if (!target) return;
    try {
      // URL は従来の関数をそのまま呼ぶ (テストがこの export を差し替える)。動画は専用の中止 API
      const res = kind === 'video' ? await cancelProductSummary(target, 'video') : await cancelProductUrlSummary(target);
      setCancelNote(res.refunded ? '読み取りを中止しました。処理が始まる前だったのでポイントは返還されます' : '読み取りを中止しました。ポイントは返還されません');
      refreshCredits();
    } catch (err) {
      const code = (err as { response?: { data?: { error_code?: string } } })?.response?.data?.error_code;
      // 既に終わっていた (完了 / 失敗) ときは中止できないが、画面の待機はやめている
      setCancelNote(code === 'JOB_NOT_INTERRUPTIBLE' ? '読み取りは既に終わっていました' : '中止の指示を送れませんでした。読み取りは続いている可能性があります');
    }
  };

  // 2ステップ: 結果があり 1 を開いていなければ 2 (反映)
  const step: UrlSummaryStepNo = phase === 'done' && result && !rereading ? 2 : 1;
  // 2 へ戻れる = 前の結果が残っていて、新しい読み取りを始めていない
  const canReturnToResult = !!result && phase !== 'running';
  const onStepChangeRef = useRef(onStepChange);
  onStepChangeRef.current = onStepChange;
  useEffect(() => {
    onStepChangeRef.current?.(step);
  }, [step]);
  useImperativeHandle(ref, () => ({ reread: () => setRereading(true) }), []);

  const goToStep = (target: UrlSummaryStepNo) => {
    if (target === step) return;
    if (target === 1) {
      setRereading(true);
      return;
    }
    if (!canReturnToResult) return;
    setError(null);
    setRereading(false);
    setPhase('done');
  };

  /** 除外・打ち切りの指示を送る。失敗したら手元の表示を送信前に戻す */
  const sendControl = async (body: { excluded_page_ids?: number[]; finish_fetch?: true }, rollback: () => void) => {
    if (!jobId) return;
    controlInFlight.current += 1;
    setControlError(null);
    try {
      const saved = await controlProductUrlSummary(jobId, body);
      setExcludedPages(new Set(saved.excluded_page_ids));
      setFinishRequested(saved.finish_fetch);
    } catch (err) {
      rollback();
      setControlError(urlSummaryRequestErrorMessage(err));
    } finally {
      controlInFlight.current -= 1;
    }
  };

  const togglePage = (pageId: number) => {
    const prev = excludedPages;
    const next = new Set(prev);
    if (next.has(pageId)) next.delete(pageId); else next.add(pageId);
    setExcludedPages(next);
    void sendControl({ excluded_page_ids: [...next] }, () => setExcludedPages(prev));
  };

  const finishFetch = () => {
    setFinishRequested(true);
    void sendControl({ finish_fetch: true }, () => setFinishRequested(false));
  };

  const plannedRows = useMemo(() => buildPlannedPageRows(live, excludedPages, finishRequested), [live, excludedPages, finishRequested]);
  const controllable = !!jobId && URL_SUMMARY_CONTROL_STAGES.has(live?.stage_key ?? '');
  const hasWaiting = plannedRows.some((r) => r.state === 'waiting');

  const toggle = (key: string, on: boolean) => {
    setAdopted((prev) => {
      const next = new Set(prev);
      if (on) next.add(key); else next.delete(key);
      return next;
    });
  };

  const choose = (key: string, value: string) => {
    setChosen((prev) => new Map(prev).set(key, value));
    toggle(key, true);
  };

  const apply = (all: boolean) => {
    if (!result?.data.proposal) return;
    const adoptSet = all ? new Set(items.filter((i) => !i.unchanged && (!i.conflict || chosen.has(i.key))).map((i) => i.key)) : adopted;
    const appeals = (result.data.proposal.appeal_candidates ?? []).filter((a) => all || adoptedAppeals.has(a));
    const adoption = applyAppeals(buildAdoption(items, adoptSet, chosen), appeals, getForm().description);
    const brand = brandChoice ? brandPair(brandChoice, brands) : null;
    const keys = [...adoption.adoptedKeys];
    if (brand) keys.push('brand_id');
    const sourcePages = buildSourcePages(result.data, keys, result.data.proposal.field_sources, result.jobId);
    onApply({ adoption, sourcePages, brand, brandMatch: result.data.brand_match ?? null, auto: false });
    if (scope) clearUrlSummaryResume(scope, resumeId);
    setApplied(true);
  };

  const data = result?.data;
  const proposal = data?.proposal ?? null;
  const productLabel = brandConfig.labels.productShort;
  const failedPages = (data?.pages ?? []).filter((p) => p.status === 'failed' || p.status === 'skipped');
  const appealCandidates = proposal?.appeal_candidates ?? [];
  const sourceText = summarySourceLabel(effectiveSource ?? null, data?.root?.final_url || data?.request?.url || url);
  const toggleAppeal = (text: string, on: boolean) => {
    setAdoptedAppeals((prev) => {
      const next = new Set(prev);
      if (on) next.add(text); else next.delete(text);
      return next;
    });
  };

  return (
    <>
    <section className={`url-summary-panel${embedded ? ' is-embedded' : ''}`} aria-label={kind === 'video' ? '動画から読み取る' : 'URLから読み取る'}>
      <div className="url-summary-head">
        {!embedded && !hideTitle && <span className="url-summary-title">{kind === 'video' ? '動画から読み取る' : 'URLから読み取る'}</span>}
        <ArrowSteps
          className="url-summary-steps"
          ariaLabel={kind === 'video' ? '動画から読み取る手順' : 'URLから読み取る手順'}
          steps={URL_SUMMARY_STEPS.map(({ step: value, label }) => ({ value, label, disabled: value === 2 ? !canReturnToResult && step !== 2 : false }))}
          current={step}
          onSelect={(value) => goToStep(value as UrlSummaryStepNo)}
        />
        <div className="url-summary-hint">
          {step === 1 ? (
            source?.kind === 'video' ? (
              <p>
                動画エディタに取り込んだ収録動画「{source.label}」の文字起こしから、AIが{productLabel}の説明文などを生成します。動画の取り込みで作った原稿を使うので、動画を送り直すことはありません。
              </p>
            ) : (
              <p>
                {productLabel}の公式サイトやLPのURLを入力すると、AIがページを読んで{productLabel}の説明文などを生成します。入力したURLに関連する複数のページも辿って読みます。
              </p>
            )
          ) : compact ? (
            <p>読み取った内容はフォームに入れました。AIが生成した文章なので、内容を確認して直してから登録してください。</p>
          ) : (
            <p>AIが生成した文章は自動的には反映しません。確認した上で反映させてください。</p>
          )}
        </div>
      </div>
      {step === 1 && source?.kind === 'video' && (
        <div className="url-summary-input-row url-summary-input-row--video">
          <span className="url-summary-video-source" data-testid="url-summary-video-source">動画「{source.label}」</span>
          {phase === 'running' ? (
            <Tooltip content="読み取りを中止します。処理が始まっていればポイントは返還されません">
              <button type="button" className="btn-modal btn-modal-sm" onClick={() => void cancel()}>中止</button>
            </Tooltip>
          ) : (
            <GenerateButton
              className="btn-ai-director-md url-summary-run"
              label="動画から読み取る"
              creditCost={creditBadge}
              onClick={() => void start()}
              disabled={disabled || !scope || creditsPerRun === null}
            />
          )}
        </div>
      )}
      {step === 1 && source?.kind !== 'video' && (
        <div className="url-summary-input-row">
          <input
            type="url"
            className="inline-edit-input url-summary-input"
            value={url}
            placeholder="https://example.jp/products/abc"
            disabled={disabled || phase === 'running'}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                e.preventDefault();
                if (phase !== 'running') void start();
              }
            }}
            aria-label="読み取るURL"
          />
          {phase === 'running' ? (
            <Tooltip content="読み取りを中止します。処理が始まっていればポイントは返還されません">
              <button type="button" className="btn-modal btn-modal-sm" onClick={() => void cancel()}>中止</button>
            </Tooltip>
          ) : (
            <GenerateButton
              className="btn-ai-director-md url-summary-run"
              label="ページを読み取る"
              creditCost={creditBadge}
              onClick={() => void start()}
              disabled={disabled || !url.trim() || !scope || creditsPerRun === null}
            />
          )}
        </div>
      )}

      {phase === 'running' && (
        <JobStepper
          stages={urlSummaryStages(live, kind === 'video' ? VIDEO_SUMMARY_STAGE_ORDER : undefined)}
          labels={kind === 'video' ? VIDEO_SUMMARY_STEPPER_LABELS : URL_SUMMARY_STEPPER_LABELS}
          stepText={progress || '読み取っています'}
          elapsed={elapsed}
          notice={deadlinePassed ? null : slowNotice}
          detail={{ fetch_pages: plannedRows.length > 0 ? `${plannedRows.filter((r) => r.state === 'read').length}/${plannedRows.length} ページ` : undefined }}
          ariaLabel={kind === 'video' ? '動画読み取りの段階' : 'URL 読み取りの段階'}
        >
          {{
            fetch_pages: plannedRows.length > 0 ? (
              <div className="url-summary-planned">
                <div className="url-summary-planned-head">
                  <span>読み込み予定のページ</span>
                  {controllable && <span className="url-summary-planned-hint">読まなくてよいページは除外できます</span>}
                </div>
                <ul className="url-summary-planned-list">
                  {plannedRows.map((row) => (
                    <li key={row.pageId} className={`url-summary-planned-row is-${row.state}`}>
                      <span className="url-summary-planned-state">
                        {row.state === 'loading' && <Loader2 size={11} className="url-summary-spinner" aria-hidden="true" />}
                        {row.state === 'read' && <Check size={12} className="url-summary-planned-check" aria-hidden="true" />}
                        {URL_SUMMARY_PAGE_STATE_LABELS[row.state]}
                      </span>
                      {row.purpose && <span className="url-summary-planned-purpose">{row.purpose}</span>}
                      <a className="url-summary-planned-label" href={row.url} target="_blank" rel="noopener noreferrer" title={row.url}>{row.label}</a>
                      {row.canToggle && (
                        <button type="button" className="url-summary-planned-toggle" onClick={() => togglePage(row.pageId)}>
                          {row.userExcluded ? <Undo2 size={12} aria-hidden="true" /> : <CircleMinus size={12} aria-hidden="true" />}
                          {row.userExcluded ? '戻す' : '除外'}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
                {controllable && (
                  <div className="url-summary-planned-actions">
                    {finishRequested ? (
                      <span className="url-summary-planned-hint">残りのページは読まずに要約します</span>
                    ) : (
                      hasWaiting && (
                        <button type="button" className="btn-modal-secondary btn-modal-secondary-sm" onClick={finishFetch}>
                          ここまででOK
                        </button>
                      )
                    )}
                  </div>
                )}
                {controlError && <div className="url-summary-error" role="alert">{controlError}</div>}
              </div>
            ) : null,
          }}
        </JobStepper>
      )}
      {phase === 'running' && deadlinePassed && (
        <p className="url-summary-note" role="status" data-testid="url-summary-deadline-note">{URL_SUMMARY_DEADLINE_NOTICE}</p>
      )}
      {error && <div className="url-summary-error" role="alert">{error}</div>}
      {cancelNote && phase === 'idle' && <p className="url-summary-note" role="status" data-testid="url-summary-cancel-note">{cancelNote}</p>}

      {step === 2 && data && compact && (
        <div className="url-summary-result url-summary-result--compact" data-testid="url-summary-compact">
          <div className="url-summary-result-head">
            <span>{sourceText} から読み取り、{appliedCount}項目をフォームに入れました。</span>
            {data.fetched_at && <span className="url-summary-meta">{new Date(data.fetched_at).toLocaleString('ja-JP')}</span>}
          </div>
          {/* AI の注意事項は既定で畳む (利用者指示 2026-10-30) */}
          {data.notes.length > 0 && (
            <details className="url-summary-notes-toggle" data-testid="url-summary-notes-toggle">
              <summary>
                <ChevronRight size={14} className="url-summary-notes-chevron" aria-hidden="true" />
                読み取り結果の注意事項 ({data.notes.length}件)
              </summary>
              <ul className="url-summary-notes">
                {data.notes.map((n, i) => <li key={`n-${i}`}>{n}</li>)}
              </ul>
            </details>
          )}
          {/* 「提案の詳細を見る」は廃止 (利用者指摘 2026-10-30): 全項目をフォームに入れた後は現在値と提案値が同じになり、
              採用を選ぶ表として意味を持たない。既存案件の編集では従来どおり詳細の表を出す */}
          {!hideRereadAction && (
            <div className="url-summary-actions url-summary-actions--compact">
              <button type="button" className="btn-modal-secondary-sm btn-quiet" onClick={() => goToStep(1)}>{kind === 'video' ? 'もう一度読み取る' : '別のURLで読み直す'}</button>
            </div>
          )}
        </div>
      )}
      {step === 2 && data && !compact && (
        <div className="url-summary-result">
          <div className="url-summary-result-head">
            <span>{sourceText} から読み取りました</span>
            {proposal?.summary_mode === 'overview' && (
              <span className="url-summary-badge" title={data.identity?.scope ?? undefined}>サイト全体の説明としてまとめました</span>
            )}
            {data.fetched_at && <span className="url-summary-meta">{new Date(data.fetched_at).toLocaleString('ja-JP')}</span>}
          </div>
          {proposal?.summary_mode === 'overview' && (
            <p className="url-summary-note url-summary-info">
              {/* 丸を塗り、i を背景色で抜く (形は lucide の Info・色は CSS)。抜いた i が細いと潰れるため線幅を 1.5 にする */}
              <Info size={15} className="url-summary-info-icon" style={{ strokeWidth: 1.5 }} aria-hidden="true" />
              <span>
                起点が一覧やブランドサイトだったため総合的な名称・説明文です。1つの{productLabel}として読み直すには、その{productLabel}のページのURLを指定してください
              </span>
            </p>
          )}

          {items.length === 0 && !data.brand_match && <p className="url-summary-note">提案できる項目がありませんでした</p>}
          {(items.length > 0 || data.brand_match) && (
            <table className="url-summary-table">
              <thead>
                <tr><th className="url-summary-col-adopt">採用</th><th>項目</th><th>現在の値</th><th className="url-summary-col-arrow" aria-hidden="true" /><th>提案値</th></tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.key} className={rowClass(item.conflict ? 'conflict' : item.unchanged ? 'unchanged' : adopted.has(item.key) ? null : 'off')}>
                    <td className="url-summary-col-adopt">
                      <input
                        type="checkbox"
                        checked={adopted.has(item.key)}
                        disabled={item.unchanged || (!!item.conflict && !chosen.has(item.key))}
                        title={item.unchanged ? '現在の値と同じです' : undefined}
                        onChange={(e) => toggle(item.key, e.target.checked)}
                        aria-label={`${item.label}を採用`}
                      />
                    </td>
                    <td className="url-summary-col-label">
                      {item.label}
                      {item.confidence === 'low' && <span className="url-summary-badge url-summary-badge-warn">要確認</span>}
                      {item.confidence === 'medium' && <span className="url-summary-badge url-summary-badge-soft">確認推奨</span>}
                    </td>
                    <td className="url-summary-col-current">{item.current.trim() ? item.current : <span className="url-summary-empty">(未入力)</span>}</td>
                    <td
                      className="url-summary-col-arrow"
                      onClick={() => { if (!item.unchanged && (!item.conflict || chosen.has(item.key))) toggle(item.key, !adopted.has(item.key)); }}
                    >
                      {item.unchanged ? <SameMark /> : <AdoptArrow connected={adopted.has(item.key)} />}
                    </td>
                    <td className="url-summary-col-proposed">
                      {item.conflict ? (
                        <div className="url-summary-conflict">
                          <span className="url-summary-conflict-label">読み取り結果が食い違っています。どちらかを選んでください</span>
                          {item.conflict.map((c, i) => (
                            <label key={`${item.key}-${i}`} className="url-summary-conflict-option">
                              <input
                                type="radio"
                                name={`conflict-${item.key}`}
                                checked={chosen.get(item.key) === c.value}
                                onChange={() => choose(item.key, c.value)}
                              />
                              <span>{c.value}</span>
                              <SourceHint labels={[sourceLabel(data, c.page_id)]} />
                            </label>
                          ))}
                        </div>
                      ) : (
                        <>
                          <span className="url-summary-proposed-text">
                            {item.proposed}
                            {item.sources.length > 0 && <SourceHint labels={item.sources.map((id) => sourceLabel(data, id))} />}
                          </span>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
                {data.brand_match && (
                  <tr className={rowClass(brandUnchanged ? 'unchanged' : brandOptions.length > 0 && brandChoice === '' ? 'off' : null)}>
                    <td className="url-summary-col-adopt">
                      <input
                        type="checkbox"
                        checked={brandChoice !== ''}
                        disabled={brandOptions.length === 0}
                        title={brandUnchanged ? '現在の値と同じです' : undefined}
                        onChange={(e) => toggleBrand(e.target.checked)}
                        aria-label="ブランドを採用"
                      />
                    </td>
                    <td className="url-summary-col-label">ブランド (登録済み)</td>
                    <td className="url-summary-col-current">{currentBrandName || <span className="url-summary-empty">(未選択)</span>}</td>
                    <td className="url-summary-col-arrow" onClick={() => { if (brandOptions.length > 0) toggleBrand(brandChoice === ''); }}>
                      {brandUnchanged ? <SameMark /> : <AdoptArrow connected={brandChoice !== ''} />}
                    </td>
                    <td className="url-summary-col-proposed">
                      <div className="url-summary-brand">
                        {brandUnchanged && <span className="url-summary-proposed-text">{currentBrandName}</span>}
                        {brandOptions.length === 1 && (
                          <span className="url-summary-proposed-text">
                            {brands.find((b) => b.id === brandOptions[0]!.brand_id)?.brand_name ?? brandOptions[0]!.brand_name}
                            {brandOptions[0]!.confidence !== 'high' && <span className="url-summary-badge url-summary-badge-warn">要確認</span>}
                            <SourceHint labels={[brandOptions[0]!.reason]} prefix="判定理由" />
                          </span>
                        )}
                        {brandOptions.length > 1 && brandOptions.map((c: UrlSummaryBrandCandidate) => (
                          <label key={c.brand_id} className="url-summary-conflict-option">
                            <input type="radio" name="url-summary-brand" checked={brandChoice === c.brand_id} onChange={() => setBrandChoice(c.brand_id)} />
                            <span>{brands.find((b) => b.id === c.brand_id)?.brand_name ?? c.brand_name}</span>
                            {c.confidence !== 'high' && <span className="url-summary-badge url-summary-badge-warn">要確認</span>}
                            <SourceHint labels={[c.reason]} prefix="判定理由" />
                          </label>
                        ))}
                        {brandPlan.notice && !brandUnchanged && <span className="url-summary-source">{brandPlan.notice}</span>}
                        {brandPlan.suggestCreate && (
                          onCreateBrand ? (
                            <span className="url-summary-brand-create">
                              <span className="url-summary-source">一致するブランドがありません</span>
                              <button
                                type="button"
                                className="btn-modal btn-modal-sm"
                                onClick={() => onCreateBrand(data.brand_match?.new_brand_draft ?? { brand_name: proposal?.brand_name ?? '', tagline: '', description: '', official_site_candidates: proposal?.brand.official_site_candidates ?? [] })}
                              >
                                新規ブランドとして作成
                              </button>
                            </span>
                          ) : (
                            <span className="url-summary-source">一致するブランドがありません。ブランドの作成は管理者に依頼してください (ブランド名の文字列は採用できます)</span>
                          )
                        )}
                      </div>
                    </td>
                  </tr>
                )}
                {appealCandidates.length > 0 && (
                  <tr className={rowClass(adoptedAppeals.size === 0 ? 'off' : null)} data-testid="url-summary-appeals">
                    <td className="url-summary-col-adopt">
                      <input
                        type="checkbox"
                        checked={adoptedAppeals.size > 0}
                        onChange={(e) => setAdoptedAppeals(e.target.checked ? new Set(appealCandidates) : new Set())}
                        aria-label="訴求候補を採用"
                      />
                    </td>
                    <td className="url-summary-col-label">訴求候補</td>
                    <td className="url-summary-col-current"><span className="url-summary-empty">(説明文の【訴求】に追加)</span></td>
                    <td className="url-summary-col-arrow" onClick={() => setAdoptedAppeals(adoptedAppeals.size > 0 ? new Set() : new Set(appealCandidates))}>
                      <AdoptArrow connected={adoptedAppeals.size > 0} />
                    </td>
                    <td className="url-summary-col-proposed">
                      <div className="url-summary-conflict">
                        {appealCandidates.map((text) => (
                          <label key={text} className="url-summary-conflict-option">
                            <input type="checkbox" checked={adoptedAppeals.has(text)} onChange={(e) => toggleAppeal(text, e.target.checked)} />
                            <span>{text}</span>
                          </label>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}

          {/* 取得元・注記 */}
          {data.sources.length > 0 && (
            <details className="url-summary-sources">
              <summary>読み取ったページ ({data.sources.length}件)</summary>
              <ul>
                {data.sources.map((s) => (
                  <li key={s.page_id}>
                    <a href={s.url} target="_blank" rel="noopener noreferrer">{s.url}</a>
                    {s.page_id === 0 && <span className="url-summary-source"> (起点)</span>}
                  </li>
                ))}
              </ul>
              {data.screenshot_url && (
                <a className="url-summary-screenshot-link" href={data.screenshot_url} target="_blank" rel="noopener noreferrer">指定ページのスクリーンショットを開く</a>
              )}
            </details>
          )}
          {(data.notes.length > 0 || failedPages.length > 0) && (
            <ul className="url-summary-notes">
              {data.notes.map((n, i) => <li key={`n-${i}`}>{n}</li>)}
            </ul>
          )}

          <div className="url-summary-actions">
            {applied && <span className="url-summary-applied">フォームに反映しました。内容を確認して保存してください</span>}
            <button type="button" className="btn-modal btn-modal-sm" onClick={() => apply(true)} disabled={items.length === 0 && !brandChoice}>
              すべて反映
            </button>
            <button type="button" className="btn-modal btn-modal-sm btn-modal-sub" onClick={() => apply(false)} disabled={adopted.size === 0 && !brandChoice}>
              選んだ項目を反映
            </button>
          </div>
        </div>
      )}
    </section>
    {phase === 'running' && !embedded && (
      <p className="url-summary-after-note">ページを離れても処理は継続します。</p>
    )}
    </>
  );
}

/** 提案値の取得元。表を詰めるためアイコンだけ出し、ホバー / フォーカスで中身を見せる */
function SourceHint({ labels, prefix = '取得元' }: { labels: string[]; prefix?: string }) {
  const text = `${prefix}: ${labels.join('・')}`;
  return (
    <span className="url-summary-source-hint" tabIndex={0} aria-label={text}>
      <Link2 size={12} aria-hidden="true" />
      <span className="url-summary-source-pop" role="tooltip">{text}</span>
    </span>
  );
}

/** 現在の値 → 提案値 の矢印。採用するときはつながり、採用しないときは途中で断たれる (操作はチェックボックスと同じ) */
function AdoptArrow({ connected }: { connected: boolean }) {
  return (
    <span className={`url-summary-arrow${connected ? ' is-connected' : ''}`} aria-hidden="true">
      <MoveRight size={20} />
      <Slash size={9} className="url-summary-arrow-cut" />
    </span>
  );
}

/** 行の状態: 食い違い (要選択) / 現在の値と同じ / 採用しない (チェックなし) */
function rowClass(state: 'conflict' | 'unchanged' | 'off' | null): string | undefined {
  return state ? `url-summary-row-${state}` : undefined;
}

/** 現在の値と提案値が同じ行の印 (矢印の代わりに「＝」) */
function SameMark() {
  return (
    <span className="url-summary-same" role="img" aria-label="現在の値と同じ" title="現在の値と同じです">
      <Equal size={16} aria-hidden="true" />
    </span>
  );
}

function sourceLabel(data: UrlSummaryResult, pageId: number): string {
  if (pageId === 0) return '指定ページ';
  const page = data.pages.find((p) => p.page_id === pageId);
  if (page?.title) return page.title.length > 24 ? `${page.title.slice(0, 24)}…` : page.title;
  const source = data.sources.find((s) => s.page_id === pageId);
  try {
    return source ? new URL(source.url).pathname : `ページ${pageId}`;
  } catch {
    return `ページ${pageId}`;
  }
}
